"use client";

import React, { useState } from "react";
import { MapPin, Clock, Phone, Navigation, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const locations = [{
  id: 1,
  name: "Vero Beach Proper (32960)",
  address: "Central Vero Beach Service Area",
  phone: "+1 (772) 555-0123",
  hours: "Mon-Sat: 08:00-18:00",
  coordinates: {
    x: 45,
    y: 55
  }
}, {
  id: 2,
  name: "Indian River Shores (32963)",
  address: "Island & Coastal Communities",
  phone: "+1 (772) 555-0124",
  hours: "Mon-Sat: 08:00-18:00",
  coordinates: {
    x: 52,
    y: 65
  }
}, {
  id: 3,
  name: "South Beach & Orchid",
  address: "North & South Barrier Island",
  phone: "+1 (772) 555-0125",
  hours: "Mon-Sat: 08:00-18:00",
  coordinates: {
    x: 65,
    y: 35
  }
}];
export default function FindNearestSalon() {
  const [activeLocation, setActiveLocation] = useState(0);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="222039" id="where-we-serve" className="bg-background py-20 lg:py-32 overflow-hidden" ref={sectionRef}>
      <div className="container mx-auto px-4 md:px-6">
        <div className={`flex flex-col md:flex-row justify-between items-start md:items-end mb-12 lg:mb-16 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
              Where We Serve
            </h2>
            <p className="text-lg text-muted-foreground font-sans max-w-xl">
              Experience the ultimate coastal clean. We serve Vero Beach and surrounding barrier island communities with meticulous attention to detail and eco-friendly practices.
            </p>
          </div>
          <div className="mt-6 md:mt-0">
            <Button variant="outline" className="rounded-full border-primary text-primary hover:bg-primary/10" asChild>
              <Link to="#">
                Book Your Clean
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {}
          <div className={`lg:col-span-5 flex flex-col gap-4 transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            {locations.map((location, i) => <div key={location.id} data-index={i} onClick={() => setActiveLocation(i)} className={`group cursor-pointer rounded-2xl border p-6 transition-all duration-300 ${activeLocation === i ? "bg-card border-primary shadow-lg" : "bg-background border-border hover:border-primary/50 hover:bg-secondary/30"}`}>
                <h3 className="text-xl font-default font-semibold text-foreground mb-4 group-hover:text-primary transition-colors">
                  {location.name}
                </h3>
                <div className="space-y-3 font-sans">
                  <div className="flex items-start gap-3 text-muted-foreground">
                    <MapPin className="w-5 h-5 shrink-0 mt-0.5 text-primary" />
                    <span>{location.address}</span>
                  </div>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Clock className="w-5 h-5 shrink-0 text-primary" />
                    <span>{location.hours}</span>
                  </div>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Phone className="w-5 h-5 shrink-0 text-primary" />
                    <span>{location.phone}</span>
                  </div>
                </div>

                <div className={`mt-6 overflow-hidden transition-all duration-300 ${activeLocation === i ? "max-h-20 opacity-100" : "max-h-0 opacity-0"}`}>
                  <Button className="w-full sm:w-auto rounded-full bg-primary text-primary-foreground hover:bg-primary/90" size="default">
                    <Navigation className="mr-2 w-4 h-4" />
                    Service Details
                  </Button>
                </div>
              </div>)}
          </div>

          {}
          <div className={`lg:col-span-7 relative min-h-[400px] lg:min-h-[600px] rounded-3xl overflow-hidden border border-border shadow-xl transition-all duration-1000 delay-400 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {}
            <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/vero_beach_service_area_map.png)] bg-muted" />

            {}
            <div className="absolute inset-0 bg-background/20 backdrop-blur-[1px]" />

            {}
            {locations.map((location, i) => <div key={`pin-${location.id}`} data-index={i} className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ease-in-out" style={{
            left: `${location.coordinates.x}%`,
            top: `${location.coordinates.y}%`,
            zIndex: activeLocation === i ? 10 : 1
          }}>
                <Button onClick={() => setActiveLocation(i)} variant="ghost" size="icon" className={`relative w-12 h-12 rounded-full transition-all duration-300 ${activeLocation === i ? "bg-primary text-primary-foreground scale-110 shadow-lg" : "bg-background text-primary border border-primary/20 shadow-sm hover:scale-105"}`}>
                  <MapPin className={`w-6 h-6 ${activeLocation === i ? "fill-primary-foreground/20" : ""}`} />

                  {}
                  {activeLocation === i && <span className="absolute inset-0 rounded-full border-2 border-primary animate-ping opacity-20" />}
                </Button>

                {}
                <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-max max-w-[200px] bg-popover text-popover-foreground text-sm font-sans px-3 py-2 rounded-lg shadow-md border border-border transition-all duration-300 pointer-events-none ${activeLocation === i ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}>
                  <p className="font-semibold">{location.name}</p>
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-popover border-t border-l border-border rotate-45" />
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}